import React, { useState } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';

import Button from '../../../components/ui/Button';

const NotificationSettings = ({ onChange }) => {
  const [emailNotifications, setEmailNotifications] = useState({
    invoiceOverdue: true,
    paymentReceived: true,
    expenseApproval: true,
    reconciliationRequired: true,
    lowBalance: true,
    reportReady: true,
    systemUpdates: false
  });

  const [alertThresholds, setAlertThresholds] = useState({
    lowBalanceAmount: '1000',
    overdueInvoiceDays: '7',
    largeTransactionAmount: '5000'
  });

  const [reportSchedule, setReportSchedule] = useState([
    {
      id: 1,
      reportType: 'profit-loss',
      frequency: 'monthly',
      recipients: 'john@accountingpro.com',
      enabled: true
    },
    {
      id: 2,
      reportType: 'cash-flow',
      frequency: 'weekly',
      recipients: 'finance@accountingpro.com',
      enabled: true
    }
  ]);

  const handleEmailToggle = (key) => {
    setEmailNotifications(prev => ({ ...prev, [key]: !prev?.[key] }));
    onChange?.();
  };

  const handleThresholdChange = (key, value) => {
    setAlertThresholds(prev => ({ ...prev, [key]: value }));
    onChange?.();
  };

  const reportTypeOptions = [
    { value: 'profit-loss', label: 'Profit & Loss' },
    { value: 'balance-sheet', label: 'Balance Sheet' },
    { value: 'cash-flow', label: 'Cash Flow Statement' },
    { value: 'aged-receivables', label: 'Aged Receivables' },
    { value: 'expense-summary', label: 'Expense Summary' }
  ];

  const frequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' }
  ];

  return (
    <div className="space-y-8">
      {/* Email Notifications */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Notifications</h3>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Checkbox
              checked={emailNotifications?.invoiceOverdue}
              onChange={() => handleEmailToggle('invoiceOverdue')}
            />
            <div>
              <label className="text-sm font-medium text-gray-900">Invoice Overdue</label>
              <p className="text-sm text-gray-600">Get notified when invoices become overdue</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              checked={emailNotifications?.paymentReceived}
              onChange={() => handleEmailToggle('paymentReceived')}
            />
            <div>
              <label className="text-sm font-medium text-gray-900">Payment Received</label>
              <p className="text-sm text-gray-600">Notification when customer payments are received</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              checked={emailNotifications?.expenseApproval}
              onChange={() => handleEmailToggle('expenseApproval')}
            />
            <div>
              <label className="text-sm font-medium text-gray-900">Expense Approval Required</label>
              <p className="text-sm text-gray-600">Alert when expenses need your approval</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              checked={emailNotifications?.reconciliationRequired}
              onChange={() => handleEmailToggle('reconciliationRequired')}
            />
            <div>
              <label className="text-sm font-medium text-gray-900">Reconciliation Required</label>
              <p className="text-sm text-gray-600">Reminder when bank accounts need reconciliation</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              checked={emailNotifications?.lowBalance}
              onChange={() => handleEmailToggle('lowBalance')}
            />
            <div>
              <label className="text-sm font-medium text-gray-900">Low Balance Alert</label>
              <p className="text-sm text-gray-600">Warning when account balance falls below threshold</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              checked={emailNotifications?.reportReady}
              onChange={() => handleEmailToggle('reportReady')}
            />
            <div>
              <label className="text-sm font-medium text-gray-900">Report Ready</label>
              <p className="text-sm text-gray-600">Notification when scheduled reports are generated</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              checked={emailNotifications?.systemUpdates}
              onChange={() => handleEmailToggle('systemUpdates')}
            />
            <div>
              <label className="text-sm font-medium text-gray-900">System Updates</label>
              <p className="text-sm text-gray-600">Product updates and new feature announcements</p>
            </div>
          </div>
        </div>
      </div>
      {/* Alert Thresholds */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Alert Thresholds</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Low Balance Amount"
            type="number"
            value={alertThresholds?.lowBalanceAmount}
            onChange={(e) => handleThresholdChange('lowBalanceAmount', e?.target?.value)}
            description="Alert when balance falls below"
          />
          <Input
            label="Overdue Invoice Days"
            type="number"
            value={alertThresholds?.overdueInvoiceDays}
            onChange={(e) => handleThresholdChange('overdueInvoiceDays', e?.target?.value)}
            description="Days before overdue alert"
          />
          <Input
            label="Large Transaction Amount"
            type="number"
            value={alertThresholds?.largeTransactionAmount}
            onChange={(e) => handleThresholdChange('largeTransactionAmount', e?.target?.value)}
            description="Alert for transactions above"
          />
        </div>
      </div>
      {/* Automated Report Delivery */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Automated Report Delivery</h3>
            <p className="text-sm text-gray-600 mt-1">Schedule reports to be sent automatically</p>
          </div>
          <Button iconName="Plus" size="sm">
            Add Schedule
          </Button>
        </div>

        <div className="space-y-4">
          {reportSchedule?.map((schedule) => (
            <div
              key={schedule?.id}
              className="bg-white border border-gray-200 rounded-lg p-4"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Checkbox checked={schedule?.enabled} />
                  <div>
                    <h4 className="font-medium text-gray-900">
                      {reportTypeOptions?.find(r => r?.value === schedule?.reportType)?.label}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {frequencyOptions?.find(f => f?.value === schedule?.frequency)?.label} • {schedule?.recipients}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" iconName="Trash2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;